import Cookies from 'js-cookie';
import { NextRequest, NextResponse } from 'next/server';
import { GetServerSidePropsContext } from 'next/types';

export type SSROrMiddleware = NextRequest | NextResponse | GetServerSidePropsContext['req'];
const isMiddleware = (ssrOrMiddleware: SSROrMiddleware): ssrOrMiddleware is NextRequest | NextResponse =>
  'get' in ssrOrMiddleware.cookies;

export const getCookie = (ssrOrMiddleware: SSROrMiddleware, cookieName: string): string | undefined =>
  isMiddleware(ssrOrMiddleware) ? ssrOrMiddleware.cookies.get(cookieName)?.value : ssrOrMiddleware.cookies[cookieName];

export const findCookie = (
  ssrOrMiddleware: SSROrMiddleware,
  predicate: (cookieName: string) => boolean,
): string | undefined => {
  if (isMiddleware(ssrOrMiddleware)) {
    return ssrOrMiddleware.cookies.getAll().find(({ name }) => predicate(name))?.value;
  }

  const allCookies = Object.entries(ssrOrMiddleware.cookies);
  const cookie = allCookies.find(([key]) => predicate(key));
  return cookie?.[1];
};

export const findCookieName = (
  ssrOrMiddleware: SSROrMiddleware,
  predicate: (cookieName: string) => boolean,
): string | undefined => {
  if (isMiddleware(ssrOrMiddleware)) {
    return ssrOrMiddleware.cookies.getAll().find(({ name }) => predicate(name))?.name;
  }

  const allCookies = Object.entries(ssrOrMiddleware.cookies);
  const cookie = allCookies.find(([key]) => predicate(key));
  return cookie?.[0];
};

export const findCookieNames = (
  ssrOrMiddleware: SSROrMiddleware,
  predicate: (cookieName: string) => boolean,
): string[] => {
  if (isMiddleware(ssrOrMiddleware)) {
    return ssrOrMiddleware.cookies
      .getAll()
      .filter(({ name }) => predicate(name))
      .map(({ name }) => name);
  }

  const allCookies = Object.entries(ssrOrMiddleware.cookies);
  return allCookies.filter(([key]) => predicate(key)).map(([key]) => key);
};

export const deleteCookie = (key: string) => Cookies.remove(key);
